"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/lib/i18n/navigation";

type Props = {
  doctorName: string;
  triggerOffset?: number;
  cta?: React.ReactNode;
};

/**
 * Sticky header compact qui apparaît quand on scroll au-delà du hero.
 * Pattern Doctolib : back ← + nom centré + favori ⭐, sur fond primary uni.
 * Mobile-only (lg:hidden), z-30 (sous le BottomNav z-50, au-dessus du contenu).
 */
export function MedecinScrollHeader({ doctorName, triggerOffset = 220, cta }: Props) {
  const [visible, setVisible] = useState(false);
  const t = useTranslations("common");

  useEffect(() => {
    function onScroll() {
      setVisible(window.scrollY > triggerOffset);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [triggerOffset]);

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-x-0 top-0 z-30 lg:hidden bg-[var(--sahha-primary-700)] shadow-md transition-all duration-200 ${
        visible ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
      }`}
      style={{ paddingTop: "max(env(safe-area-inset-top), 0px)" }}
    >
      <div className="flex h-12 items-center justify-between px-3">
        <Link
          href="/recherche"
          aria-label={t("back")}
          className="flex h-9 w-9 items-center justify-center rounded-full text-white transition active:bg-white/15"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </Link>
        <h2 className="flex-1 truncate px-2 text-[14px] font-extrabold tracking-tight text-white">
          {doctorName}
        </h2>
        {cta ? <div className="shrink-0">{cta}</div> : (
          <button
            type="button"
            aria-label={t("favorite")}
            className="flex h-9 w-9 items-center justify-center rounded-full text-white transition active:bg-white/15"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <path d="m12 17.27 6.18 3.73-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}
